import {
  USER_JURISDICTION_CLAIM,
  USER_TYPE_CLAIM,
  UserType,
} from "@/constants";
import type {
  ExternalPaymentStatus,
  InspectionPaymentResponseSchema,
  InspectionStatus,
  LicenseStatus,
  LoginUserType,
} from "@/hera-api/client/types.gen";
import { formatAddressParts, formatAddressText } from "@/lib/address-format";
import {
  ALL_INSPECTION_STATUSES,
  ALL_LICENSE_STATUSES,
  type AppLicenseStatus,
} from "@/lib/enum-constants";
import {
  getImpersonatedGovernmentJurisdictionId,
  isOperatorGovernmentImpersonating,
} from "@/lib/government-impersonate";
import { SESSION_KEYS, sessionStorage } from "@/types/session-storage";
import type { User } from "@auth0/auth0-react";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function assertUnreachable(x: never): never {
  throw new Error(`Didn't expect to get here: ${JSON.stringify(x)}`);
}

export class CustomFetchError extends Error {
  constructor(
    public error_content: unknown,
    public response: Response,
  ) {
    super();
    this.error_content = error_content;
    this.response = response;
  }
}

function _parseErrorContent(error_content: unknown): string {
  const errorObj = error_content as Record<string, unknown>;
  if (errorObj.detail && Array.isArray(errorObj.detail)) {
    // FastAPI returns an array of errors in the detail field
    // Use map to transform each error detail into a readable string
    const errorMessages = errorObj.detail.map((error) => {
      // The 'loc' array gives the location of the error.
      // We join it with '.' to make it look like a path (e.g., "body.item.name").
      const location = error.loc.join(" -> ");

      // The 'msg' is the human-readable error message.
      const message = error.msg;

      return `Location: ${location}\nMessage: ${message}`;
    });
    return errorMessages.join("\n\n");
  }
  if (errorObj.detail) {
    return errorObj.detail as string;
  }
  if (errorObj.message) {
    return errorObj.message as string;
  }

  return String(error_content);
}

export function getQueryErrorHeading(error: unknown): string {
  if (!(error instanceof CustomFetchError)) {
    return "Data load error";
  }

  const statusCode = error.response.status;

  switch (statusCode) {
    case 400:
      return "Bad Request";
    case 401:
      return "Unauthorized";
    case 403:
      return "Forbidden";
    case 404:
      return "Not Found";
    case 408:
      return "Request Timeout";
    case 409:
      return "Data Conflict";
    case 422:
      return "Invalid Input";
    case 500:
      return "Internal Server Error";
    case 502:
      return "Bad Gateway";
    case 503:
      return "Service Unavailable";
    case 504:
      return "Gateway Timeout";
    default:
      if (statusCode >= 500) {
        return "Server Error";
      }
      if (statusCode >= 400) {
        return "Client Error";
      }
      return "Data load error";
  }
}

export function getQueryErrorMessage(error: unknown): string {
  if (!error) return "";

  let stringError = String(error);
  if (error instanceof CustomFetchError) {
    stringError = _parseErrorContent(error.error_content);
  } else if (typeof error === "object" && error !== null) {
    stringError = _parseErrorContent(error);
  }

  return stringError;
}

function getApiErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== "object") return null;
  const maybeError = error as {
    status?: unknown;
    response?: { status?: unknown };
  };
  const directStatus =
    typeof maybeError.status === "number" ? maybeError.status : null;
  if (directStatus != null) return directStatus;
  const responseStatus =
    typeof maybeError.response?.status === "number"
      ? maybeError.response.status
      : null;
  return responseStatus;
}

function getApiErrorBody(error: unknown): unknown {
  if (!error || typeof error !== "object") return null;
  const maybeError = error as {
    body?: unknown;
    error?: unknown;
    data?: unknown;
    response?: { data?: unknown };
  };
  return (
    maybeError.body ??
    maybeError.error ??
    maybeError.data ??
    maybeError.response?.data ??
    null
  );
}

/**
 * Build a user-visible API error string with consistent 403 handling.
 */
export function getApiErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  const statusCode = getApiErrorStatus(error);
  const body = getApiErrorBody(error);

  if (statusCode === 403) {
    const detailFromBody = body ? _parseErrorContent(body).trim() : "";
    return (
      detailFromBody || "You do not have permission to perform this action."
    );
  }

  if (body) {
    const parsed = _parseErrorContent(body).trim();
    if (parsed) return parsed;
  }

  if (error && typeof error === "object" && "message" in error) {
    const maybeMessage = (error as { message?: unknown }).message;
    if (typeof maybeMessage === "string" && maybeMessage.trim()) {
      return maybeMessage;
    }
  }

  return fallbackMessage;
}

/** True when a cancelled license was ended via de-registration (matches stored cancellation_reason text). */
export function isDeregisteredCancellationReason(
  reason: string | null | undefined,
): boolean {
  return reason?.trim().toLowerCase() === "deregistered";
}

export function mapLicenseStatusToBadgeParams(
  status: AppLicenseStatus,
  renewalDate: string | null,
  hasAwaitingCheckPayment?: boolean,
  cancellationReason?: string | null,
  hasAwaitingInvoicePayment?: boolean,
  isRenewable?: boolean,
  forRegistrant?: boolean,
  totalDue?: string | number | null,
): {
  status: string;
  variant: "secondary" | "destructive" | "default" | "warning";
} {
  switch (status) {
    case "REQUIRES_PAYMENT":
    case "PAYMENT_FAILED":
      if (hasAwaitingInvoicePayment) {
        return { status: "Payment Pending", variant: "secondary" };
      }
      if (hasAwaitingCheckPayment) {
        return { status: "Awaiting Check", variant: "secondary" };
      }
      return { status: "Balance Due", variant: "destructive" };
    case "PAY_LATER":
      if (hasAwaitingInvoicePayment) {
        return { status: "Payment Pending", variant: "secondary" };
      }
      if (hasAwaitingCheckPayment) {
        return { status: "Awaiting Check", variant: "secondary" };
      }
      return { status: "Pay Later", variant: "warning" };
    case "PAYMENT_PROCESSING":
      return { status: "Payment Processing", variant: "secondary" };
    case "PAID_PENDING_REVIEW":
      return { status: "Pending Review", variant: "secondary" };
    case "NON_COMPLIANT_NO_RENEWAL_POSSIBLE":
      return { status: "Non-Compliant", variant: "destructive" };
    case "COMPLIANT_UNTIL_RENEWAL_DATE":
      if (isRenewable && renewalDate !== null) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const renewal = parseDateAsLocal(renewalDate);
        if (renewal) {
          renewal.setHours(0, 0, 0, 0);
          if (today > renewal) {
            return { status: "Renewal Overdue", variant: "destructive" };
          }
          return { status: "Renewal Due", variant: "warning" };
        }
      }
      return { status: "Compliant", variant: "default" };
    case "CANCELLED":
      if (Number(totalDue ?? 0) > 0) {
        if (forRegistrant) {
          return { status: "Balance Due", variant: "destructive" };
        }
        if (isDeregisteredCancellationReason(cancellationReason)) {
          return {
            status: "Deregistered - Balance Due",
            variant: "destructive",
          };
        }
        return {
          status: "Cancelled - Balance Due",
          variant: "destructive",
        };
      }
      if (isDeregisteredCancellationReason(cancellationReason)) {
        return { status: "Deregistered", variant: "secondary" };
      }
      return { status: "Cancelled", variant: "secondary" };
    case "CANCELLED_REQUIRES_PAYMENT":
      // Registrant: Balance Due; Transferred note comes from cancellation_reason.
      if (forRegistrant) {
        return { status: "Balance Due", variant: "destructive" };
      }
      return { status: "Cancelled - Balance Due", variant: "destructive" };
    case "DEREG_PAYMENT_REQUIRED":
      return {
        status: "De-registration Payment Required",
        variant: "destructive",
      };
    case "DEREG_PAYMENT_PROCESSING":
      return {
        status: "De-registration Payment Processing",
        variant: "default",
      };
    case "DEREG_PAYMENT_FAILED":
      return {
        status: "De-registration Payment Failed",
        variant: "destructive",
      };
    case "DE_REGISTRATION_PENDING":
      return { status: "De-registration Pending", variant: "secondary" };
    case "REJECTED":
      return { status: "Rejected", variant: "secondary" };
    case "EXEMPT":
      return { status: "Exempt", variant: "secondary" };
    default:
      assertUnreachable(status);
  }
}

export function mapInspectionStatusToBadgeParams(
  status: InspectionStatus,
  hasAwaitingCheckPayment?: boolean,
  isRegistrationApproved?: boolean,
  hasAwaitingInvoicePayment?: boolean,
): {
  status: string;
  variant: "secondary" | "destructive" | "default" | "warning";
} {
  if (
    hasAwaitingInvoicePayment &&
    (status === "REQUIRES_PAYMENT" || status === "PAYMENT_FAILED")
  ) {
    return { status: "Payment Pending", variant: "secondary" };
  }
  if (
    hasAwaitingCheckPayment &&
    (status === "REQUIRES_PAYMENT" || status === "PAYMENT_FAILED")
  ) {
    return { status: "Awaiting Check", variant: "secondary" };
  }
  if (status === "EXEMPT" && isRegistrationApproved === false) {
    return {
      status: "Awaiting Registration Approval",
      variant: "warning",
    };
  }
  switch (status) {
    case "REQUIRES_PAYMENT":
    case "PAYMENT_FAILED":
      return { status: "Balance Due", variant: "destructive" };
    case "PAYMENT_PROCESSING":
      return { status: "Payment Processing", variant: "secondary" };
    case "PAID_PENDING_REVIEW":
      return {
        status: "Awaiting Registration Approval",
        variant: "warning",
      };
    case "INSPECTION_PENDING":
      return { status: "Inspection Pending", variant: "warning" };
    case "COMMITTED":
      return { status: "Inspection Committed", variant: "warning" };
    case "NON_COMPLIANT":
      return { status: "Non-Compliant", variant: "destructive" };
    case "COMPLIANT":
      return { status: "Compliant", variant: "default" };
    case "CANCELLED":
      return { status: "Cancelled", variant: "secondary" };
    case "EXEMPT":
      return { status: "Exempt", variant: "secondary" };
    default:
      assertUnreachable(status);
  }
}

/** Badge variant used for status display across all portals/panels/lists */
export type StatusBadgeVariant =
  | "secondary"
  | "destructive"
  | "default"
  | "warning";

/** Display result for registration (license) status - use in all portals, panels, and lists */
export function getRegistrationStatusDisplay(
  status: AppLicenseStatus,
  renewalDate?: string | null,
  hasAwaitingCheckPayment?: boolean,
  cancellationReason?: string | null,
  hasAwaitingInvoicePayment?: boolean,
  isRenewable?: boolean,
  forRegistrant?: boolean,
  totalDue?: string | number | null,
): { label: string; variant: StatusBadgeVariant } {
  const result = mapLicenseStatusToBadgeParams(
    status,
    renewalDate ?? null,
    hasAwaitingCheckPayment,
    cancellationReason,
    hasAwaitingInvoicePayment,
    isRenewable,
    forRegistrant,
    totalDue,
  );
  return { label: result.status, variant: result.variant };
}

/**
 * License lists may show inspection status instead of registration status while
 * the license is still plain Compliant. Renewal Due / Overdue must stay visible
 * so the list matches license details.
 */
export function shouldShowInspectionStatusOnLicenseList(
  licenseStatus: LicenseStatus,
  registrationDisplayLabel: string,
): boolean {
  return (
    licenseStatus === "COMPLIANT_UNTIL_RENEWAL_DATE" &&
    registrationDisplayLabel === "Compliant"
  );
}

type InspectionForListStatus = {
  created_at: string;
  status: InspectionStatus;
  payments?: ReadonlyArray<InspectionPaymentResponseSchema> | null;
};

/** Newest inspection on a license, matching the list's newest-inspection badge. */
export function newestInspectionForListStatus<
  T extends InspectionForListStatus,
>(inspections: readonly T[] | null | undefined): T | null {
  if (!inspections?.length) return null;
  return inspections.reduce((latest, inspection) =>
    inspection.created_at > latest.created_at ? inspection : latest,
  );
}

/** Display result for inspection status - use in all portals, panels, and lists.
 * When status is EXEMPT and registration is not yet approved, shows "Awaiting Registration Approval" until approved. */
export function getInspectionStatusDisplay(
  status: InspectionStatus,
  hasAwaitingCheckPayment?: boolean,
  isRegistrationApproved?: boolean,
  hasAwaitingInvoicePayment?: boolean,
): { label: string; variant: StatusBadgeVariant } {
  const result = mapInspectionStatusToBadgeParams(
    status,
    hasAwaitingCheckPayment,
    isRegistrationApproved,
    hasAwaitingInvoicePayment,
  );
  return { label: result.status, variant: result.variant };
}

/** When inspection list flags are absent, derive from linked inspection payments. */
export function inspectionPaymentsHasAwaitingCheckPayment(
  payments: ReadonlyArray<InspectionPaymentResponseSchema> | null | undefined,
): boolean {
  if (!payments?.length) return false;
  return payments.some(
    (p) =>
      p.payment_type === "manual_adjustment" &&
      p.manual_transaction_status === "awaiting_payment" &&
      p.manual_payment_method_type === "check",
  );
}

export function inspectionPaymentsHasAwaitingInvoicePayment(
  payments: ReadonlyArray<InspectionPaymentResponseSchema> | null | undefined,
): boolean {
  if (!payments?.length) return false;
  return payments.some(
    (p) =>
      p.payment_type === "manual_adjustment" &&
      p.manual_transaction_status === "awaiting_payment" &&
      p.manual_payment_method_type === "invoice",
  );
}

export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return "";

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, "");

  // Check if we have exactly 10 digits (US phone number)
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // If not 10 digits, return the original phone number
  return phone;
}

export interface FormatCurrencyOptions {
  /** When true, whole dollar amounts display as $75 instead of $75.00 */
  hideZeroCents?: boolean;
}

export function formatCurrency(
  amount: string | number | null | undefined,
  options: FormatCurrencyOptions = {},
): string {
  if (amount === null || amount === undefined || amount === "") {
    return options.hideZeroCents ? "$0" : "$0.00";
  }

  const numAmount =
    typeof amount === "string" ? Number.parseFloat(amount) : amount;

  if (Number.isNaN(numAmount)) {
    return options.hideZeroCents ? "$0" : "$0.00";
  }

  const isWholeDollars = options.hideZeroCents && Number.isInteger(numAmount);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: isWholeDollars ? 0 : 2,
    maximumFractionDigits: isWholeDollars ? 0 : 2,
  }).format(numAmount);
}

/** Round a dollar value to 2 decimal places; returns a string suitable for inputs and API (e.g. "123.45"). */
export function roundToTwoDecimals(value: string | number): string {
  const num = typeof value === "string" ? Number.parseFloat(value) : value;
  if (Number.isNaN(num) || !Number.isFinite(num)) return "0.00";
  const rounded = Math.round(num * 100) / 100;
  return rounded.toFixed(2);
}

export function formatAddress(addressJson: string | null | undefined): string {
  if (!addressJson) {
    return "Not answered";
  }

  try {
    const addressData = JSON.parse(addressJson);
    return [
      formatAddressText(addressData.addressLine1),
      formatAddressText(addressData.addressLine2),
      formatAddressText(addressData.city, "city"),
      formatAddressText(addressData.state, "state"),
      formatAddressText(addressData.zipCode, "zipcode"),
    ]
      .filter(Boolean)
      .join(", ");
  } catch {
    // If it's not valid JSON, return as-is (for backward compatibility)
    return formatAddressText(addressJson);
  }
}

/** If the string ends with [...] or (...), return only the content inside; otherwise return the full string. */
function bracketedPartOrFull(s: string): string {
  const trimmed = s.trim();
  const squareMatch = trimmed.match(/\[([^\]]*)\]$/);
  if (squareMatch && squareMatch[1].length > 0) return squareMatch[1].trim();
  const parenMatch = trimmed.match(/\(([^)]*)\)$/);
  if (parenMatch && parenMatch[1].length > 0) return parenMatch[1].trim();
  return trimmed;
}

/**
 * Format the "question" field for display in dashboard question tables.
 * When a question title contains brackets (e.g. "Is the property insured? [OMT]"), shows only the bracketed part (e.g. "OMT").
 * API may return question as a string or (legacy) as an array; arrays stringify to "?,?" etc.
 * Always returns a single string for the column.
 */
export function formatQuestionColumnValue(question: unknown): string {
  if (question === null || question === undefined) {
    return "";
  }
  if (typeof question === "string") {
    return bracketedPartOrFull(question);
  }
  if (Array.isArray(question)) {
    return (
      question
        .map((item) =>
          item !== null && item !== undefined
            ? bracketedPartOrFull(String(item).trim())
            : "",
        )
        .filter(Boolean)
        .join(" ") || ""
    );
  }
  return bracketedPartOrFull(String(question));
}

/**
 * Format the "question" field for list/table views that should show full text.
 * Unlike formatQuestionColumnValue, this does not shorten to trailing brackets.
 */
export function formatQuestionColumnFullValue(question: unknown): string {
  if (question === null || question === undefined) {
    return "";
  }
  if (typeof question === "string") {
    return question.trim();
  }
  if (Array.isArray(question)) {
    return (
      question
        .map((item) =>
          item !== null && item !== undefined ? String(item).trim() : "",
        )
        .filter(Boolean)
        .join(" ") || ""
    );
  }
  return String(question).trim();
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Question label on the registrant fees payment summary: prefer text inside
 * trailing […] / (…) (same as dashboard columns), else the last (…) segment in the string.
 */
export function formatQuestionLabelForFeesPayment(question: unknown): string {
  if (question === null || question === undefined) {
    return "";
  }
  const s =
    typeof question === "string" ? question.trim() : String(question).trim();
  if (!s) {
    return "";
  }
  const fromColumn = formatQuestionColumnValue(question);
  if (fromColumn !== s) {
    return fromColumn;
  }
  const lastOpen = s.lastIndexOf("(");
  const lastClose = s.lastIndexOf(")");
  if (lastOpen !== -1 && lastClose > lastOpen) {
    const inner = s.slice(lastOpen + 1, lastClose).trim();
    if (inner.length > 0) {
      return inner;
    }
  }
  return s;
}

/**
 * Name/address/contact answer on the fees payment summary: show only the name when present.
 * When `companyFirst`, both parts are shown as "Company (Contact)" instead of "Name (Company)".
 */
export function formatNameAddressContactAnswerNameOnly(
  value: unknown,
  options?: { companyFirst?: boolean },
): string {
  if (value === null || value === undefined) {
    return "Not answered";
  }
  let record: Record<string, unknown> | null = null;
  if (typeof value === "string") {
    const t = value.trim();
    if (t === "") {
      return "Not answered";
    }
    try {
      const parsed: unknown = JSON.parse(t);
      if (isPlainRecord(parsed)) {
        record = parsed;
      }
    } catch {
      return formatAnswerValue(value);
    }
  } else if (isPlainRecord(value)) {
    record = value;
  }

  if (record) {
    const n = record.name;
    const nameStr =
      n != null && String(n).trim() !== "" ? String(n).trim() : "";
    const co = record.companyName ?? record.company_name;
    const coStr =
      co != null && String(co).trim() !== "" ? String(co).trim() : "";
    if (nameStr && coStr) {
      return options?.companyFirst
        ? `${coStr} (${nameStr})`
        : `${nameStr} (${coStr})`;
    }
    if (nameStr) {
      return nameStr;
    }
    if (coStr) {
      return coStr;
    }
  }
  return formatAnswerValue(value);
}

/**
 * Format name/address/contact or address-shaped answer objects (from API JSON or parsed).
 */
function formatStructuredAnswerRecord(
  o: Record<string, unknown>,
): string | null {
  const name = o.name;
  const email = o.email;
  const phone = o.phone;
  const line1 = o.addressLine1 ?? o.address1;
  const line2 = o.addressLine2 ?? o.address2;
  const city = o.city;
  const state = o.state;
  const zip = o.zipCode ?? o.zipcode ?? o.zip;
  const formattedLine1 =
    line1 == null ? line1 : formatAddressText(String(line1));
  const formattedLine2 =
    line2 == null ? line2 : formatAddressText(String(line2));
  const formattedCity =
    city == null ? city : formatAddressText(String(city), "city");
  const formattedState =
    state == null ? state : formatAddressText(String(state), "state");
  const formattedZip =
    zip == null ? zip : formatAddressText(String(zip), "zipcode");

  const companyName = o.companyName ?? o.company_name;

  const hasPerson = Boolean(
    (name != null && String(name).trim() !== "") ||
      (companyName != null && String(companyName).trim() !== "") ||
      (email != null && String(email).trim() !== "") ||
      (phone != null && String(phone).trim() !== ""),
  );
  const hasAddr = Boolean(
    (line1 != null && String(line1).trim() !== "") ||
      (line2 != null && String(line2).trim() !== "") ||
      (city != null && String(city).trim() !== "") ||
      (state != null && String(state).trim() !== "") ||
      (zip != null && String(zip).trim() !== ""),
  );

  if (hasPerson) {
    const parts: string[] = [];
    if (name != null && String(name).trim())
      parts.push(`Name: ${String(name)}`);
    if (companyName != null && String(companyName).trim())
      parts.push(`Company: ${String(companyName)}`);
    if (email != null && String(email).trim())
      parts.push(`Email: ${String(email)}`);
    if (phone != null && String(phone).trim())
      parts.push(`Phone: ${formatPhoneNumber(String(phone))}`);
    if (hasAddr) {
      const cityLine = [formattedCity, formattedState, formattedZip]
        .filter((x) => x != null && String(x).trim() !== "")
        .join(", ");
      const addrSegs = [
        formattedLine1,
        formattedLine2,
        cityLine || null,
      ].filter((s) => s != null && String(s).trim() !== "");
      if (addrSegs.length)
        parts.push(`Address: ${addrSegs.map(String).join(", ")}`);
    }
    return parts.join(" · ");
  }

  if (line1 != null && String(line1).trim()) {
    const segs = [
      formattedLine1,
      formattedLine2,
      formattedCity,
      formattedState,
      formattedZip,
    ].filter((x) => x != null && String(x).trim() !== "");
    return segs.length ? segs.map(String).join(", ") : null;
  }

  if (city != null && String(city).trim()) {
    const segs = [
      formattedLine2,
      formattedCity,
      formattedState,
      formattedZip,
    ].filter((x) => x != null && String(x).trim() !== "");
    return segs.length ? segs.map(String).join(", ") : null;
  }

  return null;
}

function humanizeAnswerKey(key: string): string {
  const spaced = key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatGenericRecordForDisplay(
  o: Record<string, unknown>,
  depth: number,
): string {
  const entries = Object.entries(o).filter(
    ([, v]) =>
      v !== null &&
      v !== undefined &&
      !(typeof v === "string" && v.trim() === ""),
  );
  if (entries.length === 0) return "Not answered";

  return entries
    .map(([k, v]) => {
      const label = humanizeAnswerKey(k);
      if (Array.isArray(v)) {
        return `${label}: ${formatAnswerValue(v, depth + 1)}`;
      }
      if (isPlainRecord(v)) {
        const structured = formatStructuredAnswerRecord(v);
        if (structured) return `${label}: ${structured}`;
        return `${label}: ${formatAnswerValue(v, depth + 1)}`;
      }
      return `${label}: ${formatAnswerValue(v, depth + 1)}`;
    })
    .join("\n");
}

/** Display label for confirmation question answers (not YES/NO). */
export function formatConfirmationAnswer(value: unknown): string {
  return value === true ? "Confirmed" : "Not confirmed";
}

export function formatAnswerValue(value: unknown, depth = 0): string {
  if (depth > 6) {
    return "…";
  }

  if (value === null || value === undefined) {
    return "Not answered";
  }

  if (typeof value === "boolean") {
    return value ? "YES" : "NO";
  }

  if (typeof value === "string") {
    // Check if this might be address JSON data or name_address_contact JSON data
    if (value.trim() !== "") {
      try {
        const parsed = JSON.parse(value);
        if (isPlainRecord(parsed)) {
          const structured = formatStructuredAnswerRecord(parsed);
          if (structured) return structured;
          if (parsed.addressLine1 || parsed.address1 || parsed.city) {
            return formatAddress(value);
          }
          return formatGenericRecordForDisplay(parsed, depth);
        }
      } catch {
        // Not valid JSON, continue with normal string handling
      }
    }
    return value.trim() === "" ? "Not answered" : value;
  }

  if (typeof value === "number") {
    return value.toString();
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "No selections";
    }
    return value.map((item) => formatAnswerValue(item, depth + 1)).join(", ");
  }

  if (isPlainRecord(value)) {
    const structured = formatStructuredAnswerRecord(value);
    if (structured) return structured;
    return formatGenericRecordForDisplay(value, depth);
  }

  // Fallback for any other types
  return String(value);
}

export function mapManualTransactionStatusToBadgeParams(
  status: "awaiting_payment" | "finalised" | "cancelled",
  manualPaymentMethodType?: string | null,
): { status: string; variant: "secondary" | "destructive" | "default" } {
  switch (status) {
    case "awaiting_payment":
      if (manualPaymentMethodType?.toLowerCase() === "invoice") {
        return { status: "Payment Pending", variant: "secondary" };
      }
      if (manualPaymentMethodType?.toLowerCase() === "check") {
        return { status: "Awaiting Check", variant: "secondary" };
      }
      return { status: "Awaiting Payment", variant: "secondary" };
    case "finalised":
      return { status: "Finalized", variant: "default" };
    case "cancelled":
      return { status: "Cancelled", variant: "destructive" };
    default:
      return { status: "Manual", variant: "secondary" };
  }
}

export function mapPaymentStatusToBadgeParams(status: ExternalPaymentStatus): {
  status: string;
  variant: "secondary" | "destructive" | "default";
} {
  return mapEffectivePaymentStatusToBadgeParams(status);
}

export function mapEffectivePaymentStatusToBadgeParams(
  status: ExternalPaymentStatus,
  options?: { netPaid?: number; grossPaid?: number },
): {
  status: string;
  variant: "secondary" | "destructive" | "default";
} {
  // Incomplete processor statuses often already have fee_paid filled in; never
  // treat them as Paid/Refunded based on allocated amounts alone.
  const isIncompleteProcessorStatus =
    status === "processing" || status === "pending" || status === "failed";

  if (!isIncompleteProcessorStatus) {
    if (
      options?.netPaid != null &&
      options?.grossPaid != null &&
      options.grossPaid > 0 &&
      options.netPaid === 0
    ) {
      return { status: "Refunded", variant: "destructive" };
    }
    if (
      options?.netPaid != null &&
      options?.grossPaid != null &&
      options.netPaid > 0 &&
      options.netPaid < options.grossPaid
    ) {
      return { status: "Partially Refunded", variant: "secondary" };
    }
    if (
      options?.netPaid != null &&
      options?.grossPaid != null &&
      options.grossPaid > 0 &&
      options.netPaid === options.grossPaid
    ) {
      return { status: "Paid", variant: "default" };
    }
  }

  switch (status) {
    case "succeeded":
      return { status: "Paid", variant: "default" };
    case "processing":
      return { status: "Payment Processing", variant: "secondary" };
    case "pending":
      return { status: "Pending", variant: "destructive" };
    case "failed":
      return { status: "Failed", variant: "destructive" };
    case "partially_refunded":
      return { status: "Partially Refunded", variant: "secondary" };
    case "refunded":
      return { status: "Refunded", variant: "destructive" };
    case "disputed":
      return { status: "Disputed", variant: "destructive" };
    case "dispute_lost":
      return { status: "Disputed", variant: "destructive" };
    case "dispute_won":
      return { status: "Paid", variant: "default" };
    default:
      assertUnreachable(status);
  }
}

/** License payment row fields used for invoice PDF download visibility. */
export type LicensePaymentRowForInvoiceDownload = {
  manual_transaction_status?: string | null;
  fee_prefilled?: string | null;
  late_fee_prefilled?: string | null;
  fee_paid?: string | null;
  late_fee_paid?: string | null;
  processing_fee?: string | null;
};

/**
 * Registration + late totals aligned with invoice Doc line items: prefilled amounts
 * while the manual transaction is awaiting payment, otherwise paid amounts.
 */
export function invoiceRegistrationLateTotalForPaymentRow(
  p: LicensePaymentRowForInvoiceDownload,
): number {
  const st = (p.manual_transaction_status ?? "").toLowerCase();
  const awaiting = st === "awaiting_payment";
  if (awaiting) {
    return (
      (Number.parseFloat(p.fee_prefilled ?? "0") || 0) +
      (Number.parseFloat(p.late_fee_prefilled ?? "0") || 0)
    );
  }
  return (
    (Number.parseFloat(p.fee_paid ?? "0") || 0) +
    (Number.parseFloat(p.late_fee_paid ?? "0") || 0)
  );
}

/**
 * True when this row has something to bill on an invoice (registration/late and/or
 * processing). Used to hide download-invoice when reg and late are both zero.
 */
export function licensePaymentRowHasBillableInvoiceFees(
  p: LicensePaymentRowForInvoiceDownload,
): boolean {
  const regLate = invoiceRegistrationLateTotalForPaymentRow(p);
  const proc = Number.parseFloat(p.processing_fee ?? "0") || 0;
  return regLate > 0 || proc > 0;
}

export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "";

  const localDate = parseDateAsLocal(dateString);
  if (localDate) {
    return localDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "Invalid date";

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Format a JS Date as local YYYY-MM-DD (not UTC). */
export function formatYmdFromLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Local calendar today as YYYY-MM-DD (not UTC). */
export function getLocalTodayYmd(): string {
  return formatYmdFromLocalDate(new Date());
}

/** Current calendar date in America/Los_Angeles as YYYY-MM-DD. */
export function getPacificTodayYmd(): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  return `${year}-${month}-${day}`;
}

/**
 * Combine property address line 1 and line 2 for single-line display (e.g. license lists).
 * When both are present, joins with ", ".
 */
export function formatPropertyAddressLines(
  address1: string | null | undefined,
  address2: string | null | undefined,
): string {
  return formatAddressParts({ address1, address2 }, { linesOnly: true });
}

/**
 * Extract YYYY-MM-DD from API date or datetime strings.
 */
export function normalizeCalendarDateString(
  value: string | null | undefined,
): string | null {
  if (!value?.trim()) return null;
  const dateMatch = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  return dateMatch ? dateMatch[0] : null;
}

/**
 * Parse an ISO date string (YYYY-MM-DD) as a local date, avoiding timezone conversion.
 *
 * When JavaScript's `new Date("2024-12-01")` parses an ISO date string,
 * it interprets it as UTC midnight. When formatted in a timezone behind UTC
 * (like Pacific Time, UTC-8), it can show the previous day.
 *
 * This function parses the date string as a local date to avoid this issue.
 *
 * @param dateString - ISO date string (YYYY-MM-DD) or null/undefined
 * @returns Date object representing the local date, or null if invalid
 */
export function parseDateAsLocal(
  dateString: string | null | undefined,
): Date | null {
  if (!dateString) return null;

  const normalized = normalizeCalendarDateString(dateString) ?? dateString;

  // Parse the date string and extract just the date part (YYYY-MM-DD)
  // This avoids timezone conversion by treating the date as a local date
  const dateMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) {
    // If it's not in YYYY-MM-DD format, fall back to regular parsing
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  }

  // Extract year, month, day and create a local date
  const [, year, month, day] = dateMatch;
  return new Date(
    Number.parseInt(year),
    Number.parseInt(month) - 1,
    Number.parseInt(day),
  );
}

export function formatDateNoTimezone(
  dateString: string | null | undefined,
): string {
  if (!dateString) return "";

  // Parse the date string and extract just the date part (YYYY-MM-DD)
  // This avoids timezone conversion by treating the date as a local date
  const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) {
    // If it's not in YYYY-MM-DD format, fall back to regular formatting
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "Invalid date";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  // Extract year, month, day and create a local date
  const [, year, month, day] = dateMatch;
  const localDate = new Date(
    Number.parseInt(year),
    Number.parseInt(month) - 1,
    Number.parseInt(day),
  );

  return localDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "Invalid date";

  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function isRenewable(license: { is_renewable: boolean }): boolean {
  return license.is_renewable;
}

export function isPayable(license: {
  is_payable: boolean;
  status?: string;
}): boolean {
  if (license.is_payable) return true;
  // Non-compliant licenses must always allow the registrant to access payment flow.
  return license.status === "NON_COMPLIANT_NO_RENEWAL_POSSIBLE";
}

export function isTransferable(license: {
  is_transferable: boolean;
}): boolean {
  return license.is_transferable;
}

export function isRejected(license: { status: string }): boolean {
  return license.status === "REJECTED";
}

export function isCancelable(license: {
  is_cancelable: boolean;
}): boolean {
  return license.is_cancelable;
}

/** Cancel available for registrant for balance-due, Pay Later, or failed payment. */
export function isCancelableForRegistrant(status: string): boolean {
  return (
    status === "REQUIRES_PAYMENT" ||
    status === "PAY_LATER" ||
    status === "PAYMENT_FAILED" ||
    status === "REJECTED"
  );
}

/** Cancel available for operator/government when status is not CANCELLED or PAYMENT_PROCESSING. */
export function isCancelableForOperatorOrGovernment(status: string): boolean {
  return status !== "CANCELLED" && status !== "PAYMENT_PROCESSING";
}

/** Registrant-initiated de-registration (excluding terminal / in-flight payment states). */
const REGISTRANT_DEREGISTER_ALLOWED_STATUSES: readonly string[] = [
  "COMPLIANT_UNTIL_RENEWAL_DATE",
  "NON_COMPLIANT_NO_RENEWAL_POSSIBLE",
  "PAID_PENDING_REVIEW",
  "REQUIRES_PAYMENT",
  "PAY_LATER",
  "PAYMENT_FAILED",
];

/** Deregistration allowed for active licenses, Pay Later / unpaid registration, renewal balance due (still compliant), or pending review — when no effective_end_date yet. */
export function isDeregisterable(license: {
  status: string;
  effective_end_date?: string | null;
}): boolean {
  return (
    REGISTRANT_DEREGISTER_ALLOWED_STATUSES.includes(license.status) &&
    (license.effective_end_date == null || license.effective_end_date === "")
  );
}

export function isReviewable(license: { is_reviewable: boolean }): boolean {
  return license.is_reviewable;
}

export function handleLicenseFormError(error: unknown, context: string) {
  const title = getQueryErrorHeading(error);
  const message = getQueryErrorMessage(error);

  return {
    title: title || `${context} Error`,
    message:
      message || `An unexpected error occurred during ${context.toLowerCase()}`,
  };
}

export function isLicenseFormError(error: unknown): error is Error {
  return error instanceof Error;
}

export function getPinColor(
  status: LicenseStatus,
  renewalDate: string | null,
): string {
  const { variant } = getRegistrationStatusDisplay(status, renewalDate);
  switch (variant) {
    case "destructive":
      return "#ef4444"; // Red
    case "secondary":
      return "#6b7280"; // Gray
    default:
      return "#3b82f6"; // Blue
  }
}

const isValidUserType = (value: string): value is UserType => {
  return Object.values(UserType).includes(value as UserType);
};

export const getUserType = (user?: User): UserType | null => {
  if (!user) {
    return null;
  }

  const userType = user[USER_TYPE_CLAIM];
  if (!isValidUserType(userType)) {
    return null;
  }

  return userType;
};

export const getGovernmentUserJurisdictionId = (user?: User): string | null => {
  if (!user) {
    return null;
  }
  const userType = getUserType(user);
  if (userType === UserType.GOVERNMENT) {
    return user[USER_JURISDICTION_CLAIM];
  }
  if (userType === UserType.OPERATOR && isOperatorGovernmentImpersonating()) {
    return getImpersonatedGovernmentJurisdictionId();
  }
  throw new Error("User is not a government user");
};

export const canUserViewJurisdiction = (
  jurisdictionId: string,
  user?: User,
): boolean => {
  if (!user) {
    return false;
  }

  const userType = getUserType(user);
  if (userType === UserType.OPERATOR) {
    if (isOperatorGovernmentImpersonating()) {
      const impersonated = getImpersonatedGovernmentJurisdictionId();
      return impersonated != null && impersonated === jurisdictionId;
    }
    return true;
  }

  if (userType === UserType.GOVERNMENT) {
    const userJurisdictionId = user[USER_JURISDICTION_CLAIM];
    if (!userJurisdictionId) {
      return false;
    }

    return userJurisdictionId === jurisdictionId;
  }

  return false;
};

export function compareDateStrings(
  dateA: string | null | undefined,
  dateB: string | null | undefined,
): number {
  const timeA = dateA ? new Date(dateA).getTime() : null;
  const timeB = dateB ? new Date(dateB).getTime() : null;

  if (timeA === null && timeB === null) {
    return 0;
  }
  if (timeA === null) {
    return 1;
  }
  if (timeB === null) {
    return -1;
  }
  return timeA - timeB;
}

export function mapUserTypeToUIString(userType: LoginUserType): string {
  switch (userType) {
    case "operator":
      return "Operator";
    case "government":
      return "Government User";
    case "registrant":
      return "Registrant";
    case "inspector":
      // Retired LoginUserType; OpenAPI may still emit until enum is dropped from PG.
      return "Inspection User";
    case "omt_ams":
      return "OMT/AMS";
    case "title_requestor":
      return "Title Requestor";
    default:
      assertUnreachable(userType);
  }
}

export function getDashboardLink(userType: UserType): string {
  switch (userType) {
    case UserType.OPERATOR:
      return "/operator/overview";
    case UserType.GOVERNMENT:
      return "/government/overview";
    case UserType.REGISTRANT:
      return "/registrant/dashboard";
    case UserType.OMT_AMS:
      return "/omt-ams/dashboard";
    case UserType.TITLE_REQUESTOR:
      return "/title/requests";
    default:
      assertUnreachable(userType);
  }
}

/**
 * Dashboard link for “Back to dashboard” and similar in-portal navigation.
 * When an operator is viewing the registrant portal via impersonation, returns
 * the registrant dashboard instead of the operator overview.
 */
export function getBackToDashboardLink(
  userType: UserType,
  pathname: string,
): string {
  const impersonationId = sessionStorage.get<string>(
    SESSION_KEYS.REGISTRANT_PORTAL_IMPERSONATE_ACCOUNT_ID,
    "",
  );
  if (
    userType === UserType.OPERATOR &&
    pathname.startsWith("/registrant") &&
    Boolean(impersonationId.trim())
  ) {
    return "/registrant/dashboard";
  }
  const omtAmsEmail = sessionStorage.get<string>(
    SESSION_KEYS.OMT_AMS_PORTAL_IMPERSONATE_EMAIL,
    "",
  );
  if (
    userType === UserType.OPERATOR &&
    pathname.startsWith("/omt-ams") &&
    Boolean(omtAmsEmail.trim())
  ) {
    return "/omt-ams/dashboard";
  }
  const governmentUserId = sessionStorage.get<string>(
    SESSION_KEYS.GOVERNMENT_PORTAL_IMPERSONATE_USER_ID,
    "",
  );
  if (
    userType === UserType.OPERATOR &&
    pathname.startsWith("/government") &&
    Boolean(governmentUserId.trim())
  ) {
    return "/government/overview";
  }
  const titleAccountId = sessionStorage.get<string>(
    SESSION_KEYS.TITLE_PORTAL_IMPERSONATE_ACCOUNT_ID,
    "",
  );
  if (
    userType === UserType.OPERATOR &&
    pathname.startsWith("/title") &&
    Boolean(titleAccountId.trim())
  ) {
    return "/title/requests";
  }
  return getDashboardLink(userType);
}

/** Ordinances workspace for operator or government admin users. */
export function getOrdinancesLink(
  userType: UserType.OPERATOR | UserType.GOVERNMENT,
): string {
  switch (userType) {
    case UserType.OPERATOR:
      return "/operator/ordinances";
    case UserType.GOVERNMENT:
      return "/government/ordinances";
    default:
      assertUnreachable(userType);
  }
}

/** Setup dashboard link for operator and government; null for registrant. */
export function getSetupLink(userType: UserType): string | null {
  switch (userType) {
    case UserType.OPERATOR:
      return "/operator/setup";
    case UserType.GOVERNMENT:
      return "/government/setup";
    case UserType.REGISTRANT:
    case UserType.OMT_AMS:
    case UserType.TITLE_REQUESTOR:
      return null;
    default:
      assertUnreachable(userType);
  }
}

/** Question configuration link for operator and government; null for registrant. */
export function getQuestionConfigurationLink(
  userType: UserType,
): string | null {
  switch (userType) {
    case UserType.OPERATOR:
      return "/operator/question-configuration";
    case UserType.GOVERNMENT:
      return "/government/question-configuration";
    case UserType.REGISTRANT:
    case UserType.OMT_AMS:
    case UserType.TITLE_REQUESTOR:
      return null;
    default:
      assertUnreachable(userType);
  }
}

export function getLicenseActionLink(
  userType: UserType,
  action: "view" | "transfer" | "cancel" | "renew" | "deregister",
  licenseId: string,
): string {
  if (userType === UserType.TITLE_REQUESTOR) {
    return "/title/requests";
  }
  const basePath =
    userType === UserType.OPERATOR
      ? "/operator"
      : userType === UserType.OMT_AMS
        ? "/omt-ams"
        : "/registrant";
  return `${basePath}/licenses/${licenseId}/${action}`;
}

// Re-export enum constants from centralized location for backward compatibility
export {
  ALL_LICENSE_STATUSES as LICENSE_STATUS_VALUES,
  ALL_INSPECTION_STATUSES,
  VALID_LICENSE_STATUS_VALUES_FOR_EDIT,
} from "./enum-constants";

/**
 * Type guard to check if a string is a valid LicenseStatus.
 * This provides runtime validation for enum values from API responses.
 */
export function isLicenseStatus(value: string): value is AppLicenseStatus {
  // Check against the readonly array from centralized constants
  for (const status of ALL_LICENSE_STATUSES) {
    if (status === value) {
      return true;
    }
  }
  return false;
}

/**
 * Type guard to check if a string is a valid InspectionStatus.
 * This provides runtime validation for enum values from API responses.
 */
export function isInspectionStatus(value: string): value is InspectionStatus {
  // Check against the readonly array from centralized constants
  for (const status of ALL_INSPECTION_STATUSES) {
    if (status === value) {
      return true;
    }
  }
  return false;
}

/**
 * Safely converts a string to LicenseStatus, normalizing to uppercase.
 * Returns the normalized value if valid, or throws an error.
 */
export function toLicenseStatus(value: string): AppLicenseStatus {
  const normalized = value.toUpperCase() as AppLicenseStatus;
  if (isLicenseStatus(normalized)) {
    return normalized;
  }
  throw new Error(
    `Invalid LicenseStatus: ${value}. Valid values are: ${ALL_LICENSE_STATUSES.join(", ")}`,
  );
}

/**
 * Safely converts a string to InspectionStatus, normalizing to uppercase.
 * Returns the normalized value if valid, or throws an error.
 */
export function toInspectionStatus(value: string): InspectionStatus {
  const normalized = value.toUpperCase();
  if (isInspectionStatus(normalized)) {
    return normalized;
  }
  throw new Error(
    `Invalid InspectionStatus: ${value}. Valid values are: ${ALL_INSPECTION_STATUSES.join(", ")}`,
  );
}

/**
 * Converts a license status to the same display label used in badges/panels/lists.
 * Use for dropdowns and any place that needs the canonical registration status text.
 */
export function getLicenseStatusLabel(status: LicenseStatus): string {
  return getRegistrationStatusDisplay(status, null, false).label;
}

/** Context for status labels that depend on renewal date or pending payments. */
export type LicenseStatusDisplayContext = {
  renewalDate?: string | null;
  isRenewable?: boolean;
  hasAwaitingCheckPayment?: boolean;
  cancellationReason?: string | null;
  hasAwaitingInvoicePayment?: boolean;
};

/**
 * Operator edit dropdown label: when stored status is Compliant but contextual logic
 * shows a different badge (e.g. Renewal Due), append the displayed status in brackets.
 */
export function getLicenseStatusEditOptionLabel(
  status: LicenseStatus,
  context?: LicenseStatusDisplayContext | null,
): string {
  const baseLabel = getLicenseStatusLabel(status);
  if (status !== "COMPLIANT_UNTIL_RENEWAL_DATE" || !context) {
    return baseLabel;
  }
  const contextualDisplay = getRegistrationStatusDisplay(
    status,
    context.renewalDate,
    context.hasAwaitingCheckPayment,
    context.cancellationReason,
    context.hasAwaitingInvoicePayment,
    context.isRenewable,
  );
  if (contextualDisplay.label !== baseLabel) {
    return `${baseLabel} (${contextualDisplay.label})`;
  }
  return baseLabel;
}

/**
 * Inspection statuses for which editing (status, dates, inspector) is allowed.
 * Edit buttons are hidden until the inspection is in one of these states.
 */
const EDITABLE_INSPECTION_STATUSES: readonly InspectionStatus[] = [
  "NON_COMPLIANT",
  "COMPLIANT",
  "CANCELLED",
  "EXEMPT",
] as const;

export function isInspectionStatusEditable(status: InspectionStatus): boolean {
  return (EDITABLE_INSPECTION_STATUSES as readonly string[]).includes(status);
}

/**
 * Converts an inspection status to the same display label used in badges/panels/lists.
 * Use for dropdowns and any place that needs the canonical inspection status text.
 */
export function getInspectionStatusLabel(
  status: InspectionStatus,
  hasAwaitingCheckPayment?: boolean,
  isRegistrationApproved?: boolean,
  hasAwaitingInvoicePayment?: boolean,
): string {
  return getInspectionStatusDisplay(
    status,
    hasAwaitingCheckPayment,
    isRegistrationApproved,
    hasAwaitingInvoicePayment,
  ).label;
}

/**
 * Downloads a file from a blob response with content-disposition header
 */
export function downloadFileFromResponse(
  blob: Blob,
  headers: Headers,
  fallbackFilename = "download",
): void {
  // Extract filename from content-disposition header
  const contentDisposition = headers.get("content-disposition");
  let filename = fallbackFilename;

  // Check for X-Filename header as fallback
  const xFilename = headers.get("x-filename");

  if (contentDisposition) {
    // First try to match filename* (UTF-8 encoded)
    const filenameStarMatch = contentDisposition.match(
      /filename\*=utf-8''([^;]+)/i,
    );
    if (filenameStarMatch?.[1]) {
      filename = decodeURIComponent(filenameStarMatch[1]);
    } else {
      // Try multiple patterns for regular filename
      const patterns = [
        /filename="([^"]+)"/i, // filename="example.xlsx"
        /filename=([^;\s]+)/i, // filename=example.xlsx
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i, // original pattern
      ];

      for (const pattern of patterns) {
        const match = contentDisposition.match(pattern);
        if (match?.[1]) {
          filename = match[1].replace(/['"]/g, "");
          break;
        }
      }
    }
  } else {
    // Try X-Filename header as fallback
    if (xFilename) {
      filename = xFilename;
    }
  }

  // Create download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the blob URL
  URL.revokeObjectURL(url);
}
